<template>
    <div class="tsi-page" v-show="ready">

        <tsi-header></tsi-header>

        <!-- Hero：轮播 + 登录卡 -->
        <section class="tsi-hero" :class="{'is-fading': fading}">
            <div class="tsi-hero__media" v-if="slideImages.length">
                <div v-for="(img, i) in slideImages" :key="img + i"
                     class="tsi-hero__slide" :class="{'is-active': i === imageIndex}"
                     :style="{backgroundImage: 'url(' + img + ')'}"></div>
                <div class="tsi-hero__fade"></div>
            </div>

            <div class="tsi-container tsi-hero__inner">
                <div class="tsi-hero__copy">
                    <div class="tsi-hero__kicker">
                        <i></i><span>{{ slide.kicker }}</span>
                    </div>
                    <h1 class="tsi-hero__title">{{ slide.title }}</h1>
                    <div class="tsi-hero__body">{{ slide.body }}</div>
                    <div class="tsi-hero__controls">
                        <div class="tsi-dots">
                            <button type="button" v-for="(s, i) in slides" :key="'dot' + i"
                                    class="tsi-dot" :class="{'is-active': i === textIndex}"
                                    :aria-label="'第 ' + (i + 1) + ' 屏'"
                                    @click="manual(i)"></button>
                        </div>
                        <div class="tsi-arrows">
                            <button type="button" class="tsi-arrow" aria-label="上一屏"
                                    @click="manual(step - 1)">←
                            </button>
                            <button type="button" class="tsi-arrow" aria-label="下一屏"
                                    @click="manual(step + 1)">→
                            </button>
                        </div>
                        <div class="tsi-counter">{{ counter }}</div>
                    </div>
                </div>

                <div class="tsi-login">
                    <div class="tsi-login__head">
                        <span class="tsi-login__head-cn">登 录</span>
                        <span class="tsi-login__head-en">SIGN IN</span>
                    </div>
                    <form action="/" @submit.prevent="submitForm">
                        <div class="tsi-login__fields">
                            <label class="tsi-field">
                                <span class="tsi-field__label">账号</span>
                                <input type="text" class="tsi-field__input" v-model="login.id"
                                       placeholder="请输入用户名 / 邮箱" autocomplete="username" required>
                            </label>
                            <label class="tsi-field">
                                <span class="tsi-field__label">密码</span>
                                <input type="password" class="tsi-field__input" v-model="login.password"
                                       placeholder="请输入密码" autocomplete="current-password" required>
                            </label>
                            <div class="tsi-login__row">
                                <label class="tsi-check">
                                    <input type="checkbox" v-model="remember">
                                    <span>记住账号</span>
                                </label>
                            </div>
                        </div>
                        <button type="submit" class="tsi-btn" :disabled="process.loading">登 录</button>
                    </form>
                    <div class="tsi-login__alt" v-if="wxCorpEnabled">
                        <a href="javascript:void(0);" class="tsi-login__alt-link" @click="jumpToWechatCorp">
                            <i class="fa fa-qrcode"></i> 企业微信扫码登录
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <!-- 系统能力 -->
        <section class="tsi-caps">
            <div class="tsi-container tsi-caps__inner">
                <div class="tsi-section-head">
                    <h2>系统能力</h2>
                    <span>CAPABILITIES</span>
                    <i></i>
                </div>
                <div class="tsi-caps__grid">
                    <div class="tsi-caps__cell" v-for="(x, i) in icons" :key="x.icon">
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#A98A4E"
                             stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" v-html="capPath(i)"></svg>
                        <div class="tsi-caps__title">{{ x.subject }}</div>
                        <p class="tsi-caps__body">{{ x.title }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- 底部入口 -->
        <section class="tsi-links">
            <div class="tsi-container tsi-links__inner">
                <a v-for="l in quickLinks" :key="l.title" class="tsi-links__card"
                   :href="l.href" :target="l.external ? '_blank' : '_self'"
                   :rel="l.external ? 'noopener' : null" @click="onQuickLink(l, $event)">
                    <div class="tsi-links__title">
                        {{ l.title }}
                        <span class="tsi-links__out" v-if="l.external">↗</span>
                    </div>
                    <div class="tsi-links__body">{{ l.body }}</div>
                </a>
            </div>
        </section>

        <tsi-footer></tsi-footer>

        <el-dialog append-to-body width="50%" title="错误" :visible.sync="show_login_error"
                   :close-on-click-modal="false">
            <div v-html="error_message"></div>
            <div slot="footer">
                <el-button type="primary" @click="jumpToSignIn">关 闭</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import SignInTsi from "@/views/mixins/sign-in-tsi"
import TsiHeader from "@/components/tsi-header"
import TsiFooter from "@/components/tsi-footer"
import "@/scss/sign-in-tsi.scss"

// 四项能力的图标，顺序对应 mixins/sign-in.js 里的 icons(稳健/高效/协作/安全)
const CAP_PATHS = [
    '<path d="M7 21V10.5l4.4-7.6c1.6.3 2.3 1.3 2.1 3L13 10h5.3c1.5 0 2.5 1.3 2.2 2.7l-1.3 6.2c-.2 1.2-1.2 2.1-2.4 2.1z"></path><rect x="2.8" y="10.5" width="4.2" height="10.5" rx="1"></rect>',
    '<circle cx="12" cy="12" r="9.2"></circle><path d="M12 6.8V12l3.6 2.2"></path>',
    '<circle cx="8" cy="8.5" r="3"></circle><circle cx="16.5" cy="15.5" r="3"></circle><path d="M2.5 20c.6-3 2.9-4.6 5.5-4.6"></path><path d="M21.5 8.5c-.6-2.4-2.6-3.8-5-3.8"></path>',
    '<path d="M12 2.8l7.5 3v6c0 4.4-3.1 8.2-7.5 9.4-4.4-1.2-7.5-5-7.5-9.4v-6z"></path><path d="M9.2 12.2l2 2 3.6-3.9"></path>',
];

export default {
    name: "sign-in-tsi",
    mixins: [SignInTsi],
    components: {TsiHeader, TsiFooter},
    data() {
        return {
            step: 0,
            fading: false,
            slides: [
                {
                    kicker: '韬视会管平台 · 服务全体成员',
                    title: '支持组织开展远程视频\n会议、培训与业务协同',
                    body: '简单易用的会议客户端，提供音视频沟通与数据共享方案，服务日常例会、培训与对外交流。'
                },
                {
                    kicker: '高清画质 · 超大容量',
                    title: '千人同时在线\n画面与声音始终清晰',
                    body: '超大容量、高清画质，兼容多终端、多系统及传统音视频设备，跨地域与海内外机构稳定连线。'
                },
                {
                    kicker: '安全可控 · 私有化部署',
                    title: '私有化部署\n会议全程安全可控',
                    body: '会管平台私有化部署，配合等候室、锁定会议与录像归档，保障业务与会议信息安全。'
                },
            ],
            quickLinks: [
                {
                    title: '软件下载',
                    body: 'Windows / macOS / iOS / Android 客户端与会议室终端插件',
                    href: 'https://meeting.tencent.com/download/',
                    external: true
                },
                {
                    title: '使用指南',
                    body: '云会管与腾讯会议客户端操作手册，随系统更新',
                    href: '/guide',
                    external: false
                },
                {
                    title: '腾讯会议',
                    body: '直接前往腾讯会议官网创建或加入会议',
                    href: 'https://meeting.tencent.com/',
                    external: true
                },
            ],
        }
    },
    computed: {
        // 文案 3 屏与图片 N 张各自循环：圆点/计数跟文案走，图片按同一步进换
        textIndex() {
            return ((this.step % this.slides.length) + this.slides.length) % this.slides.length;
        },
        imageIndex() {
            const n = this.slideImages.length;
            if (!n) return 0;
            return ((this.step % n) + n) % n;
        },
        slide() {
            return this.slides[this.textIndex];
        },
        counter() {
            return this.pad(this.textIndex + 1) + ' / ' + this.pad(this.slides.length);
        },
    },
    mounted() {
        this.startAuto();
    },
    beforeDestroy() {
        clearInterval(this.autoTimer);
        clearTimeout(this.fadeTimer);
    },
    methods: {
        pad(n) {
            return String(n).padStart(2, '0');
        },
        capPath(i) {
            return CAP_PATHS[i] || '';
        },
        startAuto() {
            clearInterval(this.autoTimer);
            this.autoTimer = setInterval(() => this.go(this.step + 1), 7000);
        },
        go(next) {
            if (next === this.step) return;
            this.fading = true;
            clearTimeout(this.fadeTimer);
            this.fadeTimer = setTimeout(() => {
                this.step = next;
                this.fading = false;
            }, 260);
        },
        manual(next) {
            this.startAuto();
            this.go(next);
        },
        onQuickLink(link, event) {
            if (link.external) return;   // 外链交给 <a target="_blank">
            event.preventDefault();
            this.$router.push(link.href);
        },
    },
}
</script>
